import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { matchPath, useHistory } from "react-router-dom";
import { BASE_URL } from "./../constants";
import { useFetch } from "./useFetch";
import { useRoutePermissionCheck } from "./useRoutePermissionCheck";

const initialState = {
  user: null,
  hotels: [],
  hotelSelect: null,
  name: null,
  email: null,
  permission: {},
  token: "loading",
  isLoggedIn: false,
  error: null,
  isLoading: false,
  inValidLogin: false,
  isFirstRender: true,
  currentHotel: null,
  notification: null,
  showNotification: false,
};

const POLL_INTERVAL = 60 * 1000; // 60 seconds
const WARNING_THRESHOLD = 60 * 5 * 1000; // 5 minute warning

const simpleReducer = (state, payload) => ({ ...state, ...payload });
const AuthContext = createContext();
const { Provider, Consumer } = AuthContext;

const AuthProvider = ({ children }) => {
  const match = matchPath(window.location.pathname, {
    path: ["/hotel/:hotelId"],
    exact: false,
    strict: false,
  });
  const matchResetPasswordUrl = matchPath(window.location.pathname, {
    path: ["/password_reset"],
    exact: false,
    strict: false,
  });

  const [state, setState] = useReducer(simpleReducer, initialState);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);

  const { apiFetch, cloneMessage, setCloneMessage } = useFetch(state.token);
  const searchValue = searchParams.get("searchHotel") || "";

  const authFetch = React.useCallback(
    (...args) =>
      new Promise((res, rej) => {
        if (state.token !== "loading") {
          res(apiFetch(...args));
        }
      }),
    [state.token, apiFetch],
  );

  const loginSession = React.useCallback(async () => {
    if (!state.token || state.token === "loading") {
      throw new Error("Token not available");
    }

    const response = await fetch(`${BASE_URL}/expiry-time`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: state.token,
      },
    });

    const data = await response.json();

    if (response.ok && data) {
      return data;
    } else {
      throw new Error("Session check failed");
    }
  }, [state.token]);

  const getAllHotels = useCallback(async () => {
    const body = {
      hotelName: searchValue,
    };
    const { get } = await authFetch({
      path: `/managing-org/all/chains/all/hotels/1`,
    });

    const { data } = await get(`?${new URLSearchParams(body)}`);

    if (data) {
      setState({ hotels: data?.hotels });
      localStorage.setItem("hotels", JSON.stringify(data));

      return data?.hotels;
    } else {
      setState({ hotels: [] });
    }
  }, [authFetch, searchValue]);

  const updateHotels = () => {
    const token = localStorage.getItem("token");
    const hotels = JSON.parse(localStorage.getItem("hotels"));
    if (token) {
      setState({
        hotels,
      });
    }
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const name = localStorage.getItem("name");
      const email = localStorage.getItem("email");
      const hotels = JSON.parse(localStorage.getItem("hotels"));
      const permission = JSON.parse(localStorage.getItem("permission"));
      const notification = localStorage.getItem("notification");
      const showNotification =
        localStorage.getItem("showNotification") === "true";
      const selectedHotel =
        hotels.find((h) => h.id === parseInt(match?.params?.hotelId)) || null;
      if (token) {
        setState({
          token,
          isLoggedIn: true,
          hotels,
          permission,
          hotelSelect: selectedHotel,
          name,
          email,
          notification,
          showNotification,
        });
      }
    } catch (e) {
      setState({
        token: null,
        isLoggedIn: false,
        hotels: [],
        hotelSelect: null,
        permission: {},
        name: null,
        email: null,
        notification: null,
        showNotification: false,
      });
    } finally {
      setState({
        isFirstRender: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!matchResetPasswordUrl) {
      return;
    }
    if (state.isFirstRender) return;
    if (state.token === null) {
      history.push("/");
      return;
    }
    if (
      state.permission[0].name === "listOfOrg" &&
      state.hotelSelect === null &&
      window.location.pathname !== "/hotel"
    ) {
      history.push("/managing_organization");
      return;
    }
    if (
      state.permission[0].name !== "listOfOrg" &&
      state.hotelSelect === null &&
      window.location.pathname !== "/hotel"
    ) {
      history.push("/choose_hotel");
      return;
    }

    if (window.location.pathname === "/hotel" && state.hotelSelect === null) {
      history.push("/hotel");
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token, state.hotelSelect, state.isFirstRender, state.permission]);

  //precium version feature defining globally
  const [version, setVersion] = useState("");
  const [rateShopFeature, setRateShopFeature] = useState(false);
  const [isHotelDetailsPending, setHotelDetailsPending] = useState(false);
  const hotelId = match?.params?.hotelId;

  const getHotelDetails = useCallback(
    async (propsHotelId) => {
      //few screens call this function with props
      setHotelDetailsPending(true);
      const { get } = await authFetch({
        path: `/hotel/${propsHotelId ?? hotelId}`,
      });
      const { data, error } = await get();
      setState({ currentHotel: { ...data } });
      if (data) {
        setVersion(data.version);
        setRateShopFeature(data.rateShopEnabled);
      } else {
        console.log(error);
      }
      setHotelDetailsPending(false);
    },
    [hotelId, authFetch],
  );

  useEffect(() => {
    if (
      matchPath(window.location.pathname, {
        path: ["/hotel/:hotelId"],
        params: {},
      })
    ) {
      getHotelDetails(hotelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch, state.token, hotelId]);

  useEffect(() => {
    getAllHotels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectHotel = (hotel, onOrganizationPage = false) => {
    let dashboard = false;
    let bob = false;
    let rateShop = false;
    setState({
      hotelSelect: hotel,
    });

    setRateShopFeature(hotel.rateShopEnabled);
    for (let key in state.permission) {
      if (state.permission.hasOwnProperty(key)) {
        if (
          state.permission[key].name === "dashboard" &&
          hotel.isPreciumEnabled
        ) {
          dashboard = true;
        }
        if (
          state.permission[key].name === "forecastView" &&
          hotel.forecastFileStatus
        ) {
          bob = true;
        }
        if (
          state.permission[key].name === "showRateshop" &&
          hotel.rateShopEnabled
        ) {
          rateShop = true;
        }
      }
    }

    const defaultURL = () => {
      if (!!dashboard) {
        return "/hotel/" + hotel.id + "/dashboard";
      } else if (!!rateShop) {
        return "/hotel/" + hotel.id + "/price-checker";
      } else if (!!bob) {
        return "/hotel/" + hotel.id + "/business_on_books";
      } else {
        return "/hotel/" + hotel.id + "/setup";
      }
    };

    if (onOrganizationPage) {
      history.push(defaultURL());
    } else if (hotel && state.hotelSelect) {
      const currentPath = window.location.pathname;
      const isOnRateshop = !!matchPath(currentPath, {
        path: "/hotel/:hotelId/price-checker",
      });
      const isOnDashboard = !!matchPath(currentPath, {
        path: "/hotel/:hotelId/dashboard",
      });

      if (isOnRateshop && !hotel.rateShopEnabled && hotel.isPreciumEnabled) {
        history.push(`/hotel/${hotel.id}/dashboard`);
      } else if (
        isOnDashboard &&
        hotel.rateShopEnabled &&
        !hotel.isPreciumEnabled
      ) {
        history.push(`/hotel/${hotel.id}/price-checker`);
      } else {
        const pathParts = currentPath.split("/");
        pathParts[2] = hotel.id;
        history.push(pathParts.join("/"));
      }
    }
  };

  const login = async ({ username, password }) => {
    try {
      setState({ isLoading: true, inValidLogin: false });
      const { token, name, email, hotels, permission } = await fetch(
        `${BASE_URL}/login`,
        {
          method: "POST",
          body: JSON.stringify({ username, password }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        },
      ).then((res) => res.json());
      if (token) {
        setState({
          isLoggedIn: true,
          hotels,
          permission,
          token,
          error: null,
          name,
          email,
        });
        localStorage.setItem("token", token);
        localStorage.setItem("name", name);
        localStorage.setItem("email", email);
        localStorage.setItem("hotels", JSON.stringify(hotels));
        localStorage.setItem("permission", JSON.stringify(permission));

        // Fetch notification after successful login
        getNotification(token);
      } else {
        setState({
          inValidLogin: true,
          isLoggedIn: false,
          hotels: [],
          token: null,
          hotelSelect: null,
          permission: {},
          name: null,
          email: null,
        });
      }
    } catch (e) {
      setState({ error: "Error logging in" });
    } finally {
      setState({ isLoading: false });
    }
  };
  const logoutApi = useCallback(async () => {
    try {
      const { del } = await authFetch({
        path: `/logout`,
      });

      await del();
    } catch (err) {
      console.log(err);
    }
  }, [authFetch]);

  const logout = () => {
    logoutApi();
    localStorage.removeItem("token");
    localStorage.removeItem("hotels");
    localStorage.removeItem("permission");
    localStorage.removeItem("file");
    localStorage.removeItem("name");
    localStorage.removeItem("email");
    localStorage.removeItem("notification");
    localStorage.removeItem("showNotification");
    setState({
      user: null,
      token: null,
      isLoggedIn: false,
      hotels: [],
      hotelSelect: null,
      permission: {},
      name: null,
      email: null,
      notification: null,
      showNotification: false,
    });
    history.push("/");
  };
  useRoutePermissionCheck(
    state?.permission ?? [],
    state.hotelSelect?.id,
    rateShopFeature,
    version === "v2",
    state?.currentHotel?.canViewUpcomingEvents ?? true,
    isHotelDetailsPending,
  );

  const getNotification = useCallback(
    async (tokenParam) => {
      const tokenToUse = tokenParam || state.token;

      if (!tokenToUse || tokenToUse === "loading") return;

      try {
        // Use direct fetch with token since authFetch might not be ready yet
        const response = await fetch(`${BASE_URL}/notification`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: tokenToUse,
          },
        });

        const data = await response.json();

        if (data && response?.ok) {
          setState({
            notification: data?.message,
            showNotification: data?.isEnabled,
          });
          localStorage.setItem("notification", data?.message ?? "");
          localStorage.setItem("showNotification", data?.isEnabled ?? false);
        }
      } catch (err) {
        console.error("Failed to fetch notification:", err);
      }
    },
    [state.token],
  );

  const closeNotification = () => {
    setState({ showNotification: false });
  };

  useEffect(() => {
    if (!state.token || state.token === "loading" || !state.isLoggedIn) {
      setSessionWarning(false);
      setTimeRemaining(null);
      return;
    }

    const checkSessionExpiry = async () => {
      try {
        const data = await loginSession();

        if (data?.expiryTime) {
          const currentTime = new Date();
          const expiryTime = new Date(data.expiryTime);
          const timeRemained = Math.ceil(expiryTime - currentTime);

          if (timeRemained <= 0) {
            logout();
            setSessionWarning(false);
          } else if (timeRemained <= WARNING_THRESHOLD) {
            setTimeRemaining(Math.floor(timeRemained));
            setSessionWarning(true);
          } else {
            setSessionWarning(false);
          }
        }
      } catch (err) {
        console.error("Session check failed:", err);
      }
    };

    if (!sessionWarning) {
      checkSessionExpiry();
    }

    const intervalId = setInterval(checkSessionExpiry, POLL_INTERVAL);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token, state.isLoggedIn]);

  return (
    <Provider
      value={{
        login,
        logout,
        logoutApi,
        selectHotel,
        authFetch,
        cloneMessage,
        setCloneMessage,
        updateHotels,
        ...state,
        version,
        setVersion,
        rateShopFeature,
        setRateShopFeature,
        getHotelDetails,
        getAllHotels,
        getNotification,
        closeNotification,
        sessionWarning,
        setSessionWarning,
        timeRemaining,
        setTimeRemaining,
      }}
    >
      {children}
    </Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { AuthProvider, Consumer as AuthConsumer, AuthContext };
